import React, { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { Armchair, Sofa, BedDouble, Hammer, Ruler, Pencil, Lamp, Frame } from 'lucide-react';

interface PageLoaderProps {
    onLoadingComplete: () => void;
}

const PageLoader: React.FC<PageLoaderProps> = ({ onLoadingComplete }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLDivElement>(null);
    const lineRef = useRef<HTMLDivElement>(null);

    useGSAP(() => {
        const tl = gsap.timeline({
            onComplete: onLoadingComplete
        });

        // Initial state
        gsap.set(containerRef.current, { autoAlpha: 1 });
        gsap.set('.logo-icon', { y: 20, autoAlpha: 0, scale: 0.9 });
        gsap.set('.logo-text', { y: 20, autoAlpha: 0 });
        gsap.set(lineRef.current, { scaleX: 0, autoAlpha: 0 });

        // Icon States
        gsap.set('.craft-icon', { autoAlpha: 0, scale: 0 });

        // --- Construction Animations (Looping while loading) ---
        // Pencil: Scribbling effect
        gsap.to('.icon-pencil', {
            x: 5,
            y: 5,
            yoyo: true,
            repeat: 5,
            duration: 0.2,
            ease: 'power1.inOut'
        });

        // Hammer: Hammering action
        gsap.fromTo('.icon-hammer',
            { rotation: -15 },
            {
                rotation: 45,
                yoyo: true,
                repeat: 5,
                duration: 0.25,
                ease: 'power1.in'
            }
        );

        // Frame: Shake when hammered
        gsap.to('.icon-frame', {
            x: 1,
            yoyo: true,
            repeat: 10,
            duration: 0.1,
            delay: 0.2
        });

        // Furniture: Pop in sequentially representing "finished"
        gsap.to('.icon-furniture', {
            scale: 1.1,
            yoyo: true,
            repeat: 1,
            duration: 0.4,
            ease: 'back.out(1.7)',
            stagger: 0.2
        });

        // --- Main Timeline (Total ~2s visible) ---
        tl.addLabel('start')

            // 1. Reveal Elements (Fast)
            .to('.craft-icon', {
                duration: 0.4,
                autoAlpha: 0.6,
                scale: 1,
                stagger: 0.05,
                ease: 'back.out(1.7)'
            }, 'start')

            // 2. Reveal Logo (Parallel)
            .to('.logo-icon', {
                duration: 0.8,
                y: 0,
                autoAlpha: 1,
                scale: 1,
                ease: 'power3.out',
            }, 'start+=0.1')

            .to('.logo-text', {
                duration: 0.6,
                y: 0,
                autoAlpha: 1,
                ease: 'power3.out',
            }, 'start+=0.3')

            .to(lineRef.current, {
                scaleX: 1,
                autoAlpha: 1,
                duration: 0.8,
                ease: 'expo.inOut'
            }, 'start+=0.4')

            // 3. Exit Sequence (Starts at ~1.8s)
            .to(['.logo-icon', '.logo-text', '.craft-icon'], {
                y: -30,
                autoAlpha: 0,
                duration: 0.4,
                ease: 'power2.in',
                delay: 0.8 // Wait just a bit to show the "construction"
            })
            .to(lineRef.current, {
                scaleX: 0,
                autoAlpha: 0,
                duration: 0.3,
                ease: 'power2.in'
            }, "<")
            .to(containerRef.current, {
                yPercent: -100,
                duration: 0.8,
                ease: 'power4.inOut'
            }, "-=0.1");

    }, { scope: containerRef });

    return (
        <div
            ref={containerRef}
            className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#F0EAD6] text-[#4A3728] overflow-hidden"
        >
            {/* Background "Making" Animations */}

            {/* Top Left: Designing (Pencil Scribbling on Ruler) */}
            <div className="absolute top-[15%] left-[15%] flex flex-col gap-4 opacity-70">
                <Ruler className="craft-icon w-8 h-8 md:w-12 md:h-12 text-[#8D6E63]" />
                <Pencil className="craft-icon icon-pencil w-6 h-6 md:w-10 md:h-10 text-[#A1887F] ml-6 -mt-4" />
            </div>

            {/* Top Right: Construction (Hammer hitting Frame) */}
            <div className="absolute top-[20%] right-[15%] flex flex-col items-end gap-2 opacity-70">
                <Hammer className="craft-icon icon-hammer w-8 h-8 md:w-12 md:h-12 text-[#6D4C41] origin-bottom-left" />
                <Frame className="craft-icon icon-frame w-10 h-10 md:w-14 md:h-14 text-[#8D6E63] mr-4" />
            </div>

            {/* Bottom Left: Finishing (Lamp illuminating) */}
            <div className="absolute bottom-[20%] left-[18%] opacity-70">
                <Lamp className="craft-icon icon-furniture w-10 h-10 md:w-16 md:h-16 text-[#795548]" />
            </div>

            {/* Bottom Right: Finished Product (Pop in) */}
            <div className="absolute bottom-[15%] right-[15%] grid grid-cols-2 gap-2 opacity-70">
                <Armchair className="craft-icon icon-furniture w-10 h-10 md:w-14 md:h-14 text-[#5D4037]" />
                <Sofa className="craft-icon icon-furniture w-12 h-12 md:w-16 md:h-16 text-[#4E342E]" />
            </div>

            {/* Center Content */}
            <div ref={textRef} className="relative z-10 flex flex-col items-center gap-6 mb-8">
                {/* Logo Icon */}
                <img
                    src="https://kwvacrehozikjyxymprc.supabase.co/storage/v1/object/public/product-images/1.png"
                    alt="D'mans Logo"
                    className="logo-icon h-28 w-auto object-contain will-change-transform drop-shadow-sm"
                />

                {/* Logo Text */}
                <img
                    src="https://kwvacrehozikjyxymprc.supabase.co/storage/v1/object/public/product-images/2.png"
                    alt="D'mans Text"
                    className="logo-text h-10 w-auto object-contain will-change-transform opacity-90 mix-blend-multiply"
                />
            </div>

            <div
                ref={lineRef}
                className="w-48 h-[1.5px] bg-[#8D6E63]/40 origin-center rounded-full"
            />
        </div>
    );
};

export default PageLoader;
